import { css } from 'aphrodite';
import moment from 'moment';
import React from 'react';
import { FormProvider, useForm, useFormContext as useParentFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { IEventRegistrationSurvey, IFormField } from '../../../../../extViewmodels';
import { PaymentSummary } from '../../../../../surveys/containers/SurveyResponses/EventRegistration/components/EventSurvey/EventSurveyForm/components/PaymentSummary';
import { getRsvpBackground } from '../../../../../surveys/containers/SurveyResponses/EventRegistration/components/EventSurvey/EventSurveyForm/utils';
import { IEventTicket as ISurveyEventTicket } from '../../../../../surveys/models/Api';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { styleSheet } from '../styles';
import { usePreviewPaymentProcessing } from './preview/hooks/usePreviewPaymentProcessing';
import { PreviewAttendanceRadioButtons } from './preview/PreviewAttendanceRadioButtons';
import { PreviewAttendeeInfoFields } from './preview/PreviewAttendeeInfoFields';
import { PreviewCustomFormFields } from './preview/PreviewCustomFormFields';
import { PreviewFormActions } from './preview/PreviewFormActions';
import { PreviewGuestFields } from './preview/PreviewGuestFields';
import { PreviewTicketHolderFields } from './preview/PreviewTicketHolderFields';
import { PreviewTicketSelector } from './preview/PreviewTicketSelector';
import { DEFAULT_VALUES, IFormValues } from './preview/types';

interface PreviewEventSurveyFormProps {
	survey: IEventRegistrationSurvey;
	formValues?: {
		eventTickets?: {
			id?: string;
			name?: string;
			description?: string;
			unitPrice?: number;
			maxAllowed?: number | null;
			attendeeCountPerItem?: number;
			isDefault?: boolean;
			hideTicket?: boolean;
			showAsSoldOut?: boolean;
		}[];
		isPaidEvent?: boolean;
		payments?: {
			cost?: number;
		};
	};
	enableRequestAttendeesToRSVP?: boolean;
}

export const PreviewEventSurveyForm: React.FC<PreviewEventSurveyFormProps> = ({
	survey: baseSurvey,
	formValues: liveFormValues,
	enableRequestAttendeesToRSVP = false,
}) => {
	// Get parent form context for settings (customForm, payments, etc.)
	const parentFormContext = useParentFormContext();
	const parentFormValues = parentFormContext.watch();
	const customFormFields = (parentFormValues.customForm?.fields ?? []) as IFormField<string>[];
	const guestLimit = enableRequestAttendeesToRSVP && parentFormValues.setGuestLimit ? parentFormValues.guestLimit : 0;

	// Merge saved survey data with live form values for real-time preview
	const survey = React.useMemo(() => {
		if (!baseSurvey) return baseSurvey;

		const mergedSurvey = { ...baseSurvey };

		// Update isPaidEvent from live form values
		if (liveFormValues?.isPaidEvent !== undefined) {
			mergedSurvey.isPaidEvent = liveFormValues.isPaidEvent;
		}

		// Merge eventTickets from live form values (live updates)
		if (liveFormValues?.eventTickets && liveFormValues.eventTickets.length > 0) {
			mergedSurvey.attendeeOptions = {
				...mergedSurvey.attendeeOptions,
				eventTickets: liveFormValues.eventTickets as unknown as typeof mergedSurvey.attendeeOptions.eventTickets,
				// Clear costPerAttendee when using tiered tickets
				costPerAttendee: undefined,
			};
		} else if (liveFormValues?.payments?.cost && liveFormValues.isPaidEvent) {
			// Legacy: use payments.cost for costPerAttendee
			mergedSurvey.attendeeOptions = {
				...mergedSurvey.attendeeOptions,
				costPerAttendee: liveFormValues.payments.cost * 100, // Convert to cents
				eventTickets: undefined,
			};
		}

		return mergedSurvey;
	}, [baseSurvey, liveFormValues]);

	// Initialize React Hook Form
	const methods = useForm<IFormValues>({
		defaultValues: DEFAULT_VALUES,
		mode: 'onChange',
	});

	const { watch, setValue, getValues, control, formState } = methods;
	const formValues = watch();
	const isAttending = formValues.isAttending;

	// Track primary ticket holder (for tiered tickets)
	const [primaryTicketHolderId, setPrimaryTicketHolderId] = React.useState<string | null>(null);

	// Track survey ID to reset initialization when survey changes
	const [initializedSurveyId, setInitializedSurveyId] = React.useState<string | null>(null);

	// Initialize form with survey data (reset when survey changes)
	React.useEffect(() => {
		if (!survey) return;

		const surveyId = survey.id || 'default';
		// Skip if already initialized for this survey
		if (initializedSurveyId === surveyId) return;

		// For tiered tickets: ensure ticketSelections starts as empty object (all tickets at 0)
		// This matches the live survey behavior where tickets start unselected
		if (survey?.isPaidEvent && survey?.attendeeOptions?.eventTickets?.length) {
			setValue('ticketSelections', {});
			setValue('ticketHolders', []);
		}

		// For legacy events: initialize guests array
		if (
			survey?.isPaidEvent &&
			survey?.attendeeOptions?.costPerAttendee &&
			!survey?.attendeeOptions?.eventTickets?.length
		) {
			setValue('bringingGuest', false);
			setValue('guests', [{ firstName: '', lastName: '', isPaid: false }]);
		}

		setInitializedSurveyId(surveyId);
	}, [survey, survey?.id, setValue, initializedSurveyId]);

	const handleAttendingYes = React.useCallback(() => {
		setValue('isAttending', true);
	}, [setValue]);

	const handleAttendingNo = React.useCallback(() => {
		setValue('isAttending', false);
	}, [setValue]);

	const isPastEvent = React.useMemo(() => {
		const dueDateString = survey?.attendeeOptions?.registrationDeadline || survey?.eventInformation?.startTime;
		return dueDateString ? moment(dueDateString).isBefore(new Date()) : false;
	}, [survey]);

	const stats = survey?.stats?.responseStats?.find(x => x.category === 'Total');

	const isEventFull =
		stats && survey?.attendeeOptions?.maximumCapacity
			? stats.attending >= survey.attendeeOptions.maximumCapacity
			: false;

	const rsvpBackground = React.useMemo(
		() => getRsvpBackground(isAttending, isPastEvent, isEventFull),
		[isAttending, isPastEvent, isEventFull]
	);

	const handlePreviewSubmit = React.useCallback(() => {
		const shouldShowPayment = isAttending && survey?.isPaidEvent;
		toast.success(shouldShowPayment ? 'Payment flow started (Preview only)' : 'Preview form submitted');
	}, [isAttending, survey?.isPaidEvent]);

	// Determine if this is a tiered ticket event or legacy event
	const isTieredTicketEvent = React.useMemo(() => {
		return Boolean(
			survey?.isPaidEvent && survey?.attendeeOptions?.eventTickets && survey?.attendeeOptions?.eventTickets?.length > 0
		);
	}, [survey]);

	const isLegacyEvent = React.useMemo(() => {
		return Boolean(survey?.attendeeOptions?.costPerAttendee && !survey?.attendeeOptions?.eventTickets?.length);
	}, [survey]);

	// Handler for ticket selection changes - updates form state for preview
	const handleTicketSelectionChange = React.useCallback(
		(ticketId: string, quantity: number) => {
			// Use getValues() to get the latest state, not stale watch() value
			const currentValues = getValues();
			const newSelections = { ...(currentValues.ticketSelections || {}) };
			if (quantity === 0) {
				delete newSelections[ticketId];
			} else {
				newSelections[ticketId] = quantity;
			}
			setValue('ticketSelections', newSelections);
		},
		[getValues, setValue]
	);

	// Handler for primary ticket holder change - updates state for preview
	const handlePrimaryTicketHolderChange = React.useCallback((id: string) => {
		setPrimaryTicketHolderId(id);
	}, []);

	// Synchronize ticketHolders array with ticket selections (similar to live survey)
	// This effect runs when ticketSelections change to update ticketHolders dynamically
	React.useEffect(() => {
		if (!isTieredTicketEvent || !survey?.attendeeOptions?.eventTickets || !initializedSurveyId) return;

		const ticketSelections = formValues.ticketSelections || {};
		const currentTicketHolders = formValues.ticketHolders || [];

		// Calculate required tickets per ticket type (excluding hidden/sold-out)
		const requiredTicketCounts: Record<string, number> = {};
		Object.entries(ticketSelections).forEach(([ticketId, quantity]) => {
			const ticket = survey.attendeeOptions.eventTickets?.find(t => t.id === ticketId);
			if (ticket && !ticket.hideTicket && !ticket.showAsSoldOut && quantity > 0) {
				requiredTicketCounts[ticketId] = quantity;
			}
		});

		// Count current holders per ticket type
		const currentTicketCounts: Record<string, number> = {};
		currentTicketHolders.forEach(holder => {
			currentTicketCounts[holder.ticketId] = (currentTicketCounts[holder.ticketId] || 0) + 1;
		});

		// Check if adjustment is needed
		let needsAdjustment = false;
		const allTicketIds = new Set([...Object.keys(requiredTicketCounts), ...Object.keys(currentTicketCounts)]);

		for (const ticketId of allTicketIds) {
			const required = requiredTicketCounts[ticketId] || 0;
			const current = currentTicketCounts[ticketId] || 0;
			if (required !== current) {
				needsAdjustment = true;
				break;
			}
		}

		if (needsAdjustment) {
			let newHolders = [...currentTicketHolders];

			// Process each ticket type separately
			Object.entries(requiredTicketCounts).forEach(([ticketId, requiredCount]) => {
				const ticket = survey.attendeeOptions.eventTickets?.find(t => t.id === ticketId);
				if (!ticket) return;

				const currentCount = newHolders.filter(h => h.ticketId === ticketId).length;

				if (requiredCount > currentCount) {
					// Add new holders with generated IDs
					const toAdd = requiredCount - currentCount;
					for (let i = 0; i < toAdd; i++) {
						newHolders.push({
							id: crypto.randomUUID(), // Generate stable ID
							ticketId,
							ticketName: ticket.name || 'Ticket',
							firstName: '',
							lastName: '',
							email: '',
							phone: '',
						});
					}
				} else if (requiredCount < currentCount) {
					// Remove holders from the end for this ticket type
					const toRemove = currentCount - requiredCount;
					let removed = 0;

					// Remove from the end, keeping holders at the beginning
					newHolders = newHolders.filter(holder => {
						if (holder.ticketId !== ticketId) return true;
						if (removed < toRemove) {
							removed++;
							return false;
						}
						return true;
					});
				}
			});

			// Remove holders for tickets that are no longer selected
			newHolders = newHolders.filter(holder => requiredTicketCounts[holder.ticketId] > 0);

			setValue('ticketHolders', newHolders);

			// Update primary ticket holder if it was removed
			if (primaryTicketHolderId) {
				const primaryStillExists = newHolders.some(h => h.id === primaryTicketHolderId);
				if (!primaryStillExists && newHolders.length > 0) {
					setPrimaryTicketHolderId(newHolders[0].id || null);
				} else if (newHolders.length === 0) {
					setPrimaryTicketHolderId(null);
				}
			} else if (newHolders.length > 0) {
				// Set first holder as primary if none is set
				setPrimaryTicketHolderId(newHolders[0].id || null);
			}
		}
	}, [
		isTieredTicketEvent,
		survey?.attendeeOptions?.eventTickets,
		formValues.ticketSelections,
		formValues.ticketHolders,
		setValue,
		primaryTicketHolderId,
		initializedSurveyId,
	]);

	// Payment processing for preview (no actual payment submission)
	const { ticketsOrder, formattedAmount, paidTickets, getPaidTicketQuantities } = usePreviewPaymentProcessing({
		survey,
		formValues,
	});

	if (!survey) {
		return <LoadingSpinner type='small' data-testid='loading-spinner' />;
	}

	let surveyWarningText = '';
	if (isPastEvent) {
		surveyWarningText = 'Sorry, the event registration deadline has passed.';
	}
	if (isEventFull) {
		surveyWarningText = 'Sorry, the event has reached the maximum capacity of attendees.';
	}
	surveyWarningText = `${surveyWarningText} Please reach out to the event organizer for details.`;

	return (
		<FormProvider {...methods}>
			<div className={css(styleSheet.rsvpForm)} data-testid='rsvp-form'>
				{!isPastEvent && !isEventFull && (
					<PreviewAttendeeInfoFields
						requirePhoneNumber={enableRequestAttendeesToRSVP && parentFormValues.requirePhoneNumber}
					/>
				)}
				<PreviewAttendanceRadioButtons
					rsvpBackground={rsvpBackground}
					isPastEvent={isPastEvent}
					isEventFull={isEventFull}
					surveyWarningText={surveyWarningText}
					onAttendingYes={handleAttendingYes}
					onAttendingNo={handleAttendingNo}
				/>

				{/* Tiered Ticket Support - TicketSelector and TicketHolderFields */}
				{isAttending && isTieredTicketEvent && survey?.attendeeOptions?.eventTickets ? (
					<>
						<PreviewTicketSelector
							tickets={survey.attendeeOptions.eventTickets as unknown as ISurveyEventTicket[]}
							selections={formValues.ticketSelections || {}}
							onSelectionChange={handleTicketSelectionChange}
						/>
						<PreviewTicketHolderFields
							tickets={survey.attendeeOptions.eventTickets as unknown as ISurveyEventTicket[]}
							ticketSelections={formValues.ticketSelections || {}}
							ticketHolders={formValues.ticketHolders || []}
							control={control}
							errors={formState.errors}
							primaryTicketHolderId={primaryTicketHolderId}
							onPrimaryTicketHolderChange={handlePrimaryTicketHolderChange}
							requireGuestNames={survey.attendeeOptions.requireGuestNames}
							requirePhoneNumber={survey?.attendeeOptions?.requirePhoneNumber}
						/>
					</>
				) : null}

				{/* Guest Support - show for any event with guest limit (not just legacy events) */}
				{guestLimit && enableRequestAttendeesToRSVP && isAttending && !isTieredTicketEvent ? (
					<PreviewGuestFields guestLimit={guestLimit} requireGuestNames={survey?.attendeeOptions?.requireGuestNames} />
				) : null}
				{isAttending ? <PreviewCustomFormFields customFormFields={customFormFields} /> : null}

				{/* Payment Summary Component */}
				{isAttending && survey?.isPaidEvent ? (
					<PaymentSummary
						ticketSelections={formValues.ticketSelections}
						tickets={survey.attendeeOptions.eventTickets as unknown as ISurveyEventTicket[]}
						ticketsOrder={ticketsOrder}
						paidTickets={paidTickets}
						formattedAmount={formattedAmount}
						getPaidTicketQuantities={getPaidTicketQuantities}
						isLegacyEvent={isLegacyEvent}
						isPaidEvent={survey.isPaidEvent}
						costPerAttendee={survey.attendeeOptions.costPerAttendee}
					/>
				) : null}
				<PreviewFormActions
					isAttending={isAttending}
					isPaidEvent={survey?.isPaidEvent || false}
					onSubmit={handlePreviewSubmit}
				/>
			</div>
		</FormProvider>
	);
};
